// apolo/src/lib/loop/dagPlanBridge.js — harness sweep #9 + #5.
// @arch:pure-engine
//
// PONTE PURA entre o executor DAG (taskExecutor) e a PlanState/UI. Extraído de
// SessionWorkspace pra manter o shell fino (dp-loc apertado) e ser unit-testável.
//
// #9 — o TodoDropdown ficava dessincronizado do progresso real: os todos só eram
// atualizados NO FIM (run_result), então durante a execução do DAG aparecia "0/N"
// mesmo com tasks já verdes. O executor JÁ emite eventos por-task (task_started /
// task_oracle_pass / task_oracle_fail) com taskId; aqui mapeamos pro todo_status que
// dirige o TodoDropdown ao VIVO. O id do todo é `t${n}` e o da task é `${n}` (mesmo
// step), então o mapa é `t` + taskId.
//
// #5 — o executor DAG descartava as respostas do usuário às perguntas do plano: elas
// vão pro bloco Requirements do spec, mas os prompts por-task (baseRunTask) usam só
// name/acceptance. extractUserDecisions puxa o bloco "Respostas do usuário" do texto de
// aprovação (buildPlanApprovalText) pra ser prefixado em CADA task do DAG.

const TASK_EVENT_TO_STATUS = {
  task_started: 'active',
  task_oracle_pass: 'done',
  task_oracle_fail: 'blocked',
  task_blocked: 'blocked', // WSX — task não pôde executar (permissão/timeout): mostra 'blocked', não some
  task_reset: 'pending', // fix B — rollback de wave paralela: a task volta pra fila (re-roda serial)
};

// WSX (forense Pro Esporte) — stopReasons em que a TASK do DAG NÃO conseguiu executar:
// bloqueio de permissão/segurança, timeout absoluto, Stop, sem-worktree, overflow de
// contexto, espiral de falha de tools, ou o modelo estagnou sem chamar tool. Nenhum é
// "código verificado e reprovado" (vermelho de oracle) — são "não deu pra rodar", i.e.
// RECUPERÁVEIS. Marcar como failed envenenaria o learning e mostraria "task_failed"
// pra um soluço de permissão. (Um 'max_iterations'/'autocorrect_cap'/'unbacked_green'
// NÃO entra aqui: o oracle deve julgar o que foi produzido.)
// não-exportado: uso interno via isBlockedStopReason (evita export morto/knip).
const BLOCKED_STOP_REASONS = new Set([
  'llm_timeout', 'aborted', 'exception', 'no_chat_fn', 'no_root',
  'context_overflow', 'tool_failure_spiral', 'stall_gave_up',
  'task_timeout', // fix H — teto wall-clock POR TASK (taskDeadline.js): recuperável, não oracle-red
]);

/** True quando o stopReason do runLocalAgentLoop é um bloqueio recuperável (não oracle-red). */
export function isBlockedStopReason(stopReason) {
  return BLOCKED_STOP_REASONS.has(String(stopReason || ''));
}

/**
 * WSX — envolve o resultado do runLocalAgentLoop de UMA task do DAG: marca `blocked`
 * quando o stopReason é um bloqueio recuperável (não vermelho de oracle), pro executor
 * roteá-la ao bucket blocked em vez de failed. Pass-through quando não é bloqueio.
 */
export function markBlockedTaskOutcome(r) {
  return isBlockedStopReason(r?.stopReason) ? { ...r, blocked: true, blockedReason: r.stopReason } : r;
}

/**
 * WSX — linha de resumo do DAG (pura): concluído / bloqueio RECUPERÁVEL (permissão/
 * timeout — acionável, não catastrófico) / bloqueio duro. Extraída do SessionWorkspace
 * (spine) pra manter o dp-loc no baseline.
 * @param {{ blocked?: boolean, reason?: string, done?: string[] }} dagRes
 * @param {Array<any>} dagTasks
 */
export function dagSummaryLine(dagRes, dagTasks) {
  const total = Array.isArray(dagTasks) ? dagTasks.length : 0;
  const green = Array.isArray(dagRes?.done) ? dagRes.done : [];
  const greenList = green.join(', ') || 'nenhuma';
  if (!dagRes?.blocked) {
    return `✅ DAG concluído — ${green.length}/${total} tasks verdes (honest-green por task).`;
  }
  if (dagRes.reason === 'user_stop') {
    return `⏹️ DAG interrompido por você. O progresso confirmado foi preservado. Tasks verdes: ${greenList}.`;
  }
  if (dagRes.reason === 'memory_pressure') {
    return `⏸️ DAG pausado para proteger o Apolo de pressão de memória. O progresso confirmado foi preservado; feche apps/abas pesados e continue. Tasks verdes: ${greenList}.`;
  }
  if (dagRes.reason === 'run_idle_timeout') {
    return `⏱️ DAG pausado porque o turno ficou sem atividade. O progresso confirmado foi preservado; tente novamente ou divida a task longa. Tasks verdes: ${greenList}.`;
  }
  if (dagRes.reason === 'session_deleted') {
    return `⏹️ DAG encerrado porque a sessão foi removida. Tasks verdes: ${greenList}.`;
  }
  if (dagRes.reason === 'aborted') {
    return `⏸️ DAG interrompido por um cancelamento sem origem identificada. O progresso confirmado foi preservado; tente novamente. Tasks verdes: ${greenList}.`;
  }
  if (dagRes.reason === 'blocked_permission') {
    return `⏸️ DAG pausado: uma tarefa não pôde executar (bloqueio de permissão/segurança, timeout ou parada) — não é falha do código. Nada do que rodou foi perdido: libere/ajuste e reexecute. Tasks verdes: ${greenList}.`;
  }
  return `⚠️ DAG bloqueado (${dagRes.reason}). Tasks verdes: ${greenList}.`;
}

/**
 * Mapeia um evento do taskExecutor para um update de todo_status da PlanState.
 * @param {{ type?: string, taskId?: string }} evt
 * @returns {{ id: string, status: 'pending'|'active'|'done'|'blocked', advance: boolean } | null}
 */
export function taskEventToTodoStatus(evt) {
  const status = evt && TASK_EVENT_TO_STATUS[evt.type];
  if (!status) return null;
  const taskId = evt.taskId != null ? String(evt.taskId) : '';
  if (!taskId) return null;
  // Já veio com prefixo? (defensivo) — não duplica.
  const id = taskId.startsWith('t') ? taskId : `t${taskId}`;
  // advance:false (review adversarial) — o AUTO-advance do reducer assume execução
  // serial ("done ⇒ próximo pending vira active"); numa wave paralela isso promovia
  // um todo que o executor NEM começou. O executor emite task_started de verdade —
  // o board segue os eventos, nunca a suposição.
  return { id, status, advance: false };
}

/** id de todo (`t${n}`) a partir do id de task (`${n}`), sem duplicar prefixo. */
const todoIdFor = (taskId) => {
  const s = String(taskId);
  return s.startsWith('t') ? s : `t${s}`;
};

/**
 * RCA lentidão fix I — mapeia um evento do LOOP de uma task pro todo_progress da
 * PlanState ("passo N · tool" na linha ativa do TodoDropdown). PURO e POR TASK
 * (o taskId entra por fora), então continua correto quando o fix B roda tasks em
 * paralelo — nada de "task atual" compartilhada. null pra eventos sem progresso.
 * @param {string} taskId
 * @param {{ type?: string, n?: number, name?: string }} evt  evento do runLocalAgentLoop
 * @returns {{ type:'todo_progress', id:string, step?:number, tool?:string } | null}
 */
export function loopEventToTodoProgress(taskId, evt) {
  if (taskId == null || taskId === '' || !evt) return null;
  if (evt.type === 'loop_step') {
    const step = Number(evt.n);
    return Number.isFinite(step) ? { type: 'todo_progress', id: todoIdFor(taskId), step } : null;
  }
  if (evt.type === 'tool_call' && typeof evt.name === 'string' && evt.name) {
    return { type: 'todo_progress', id: todoIdFor(taskId), tool: evt.name };
  }
  return null;
}

/**
 * fix I/B — sink de eventos de UMA task do DAG: despacha o todo_progress (quando
 * houver) e encaminha o evento pro onEvent principal — exceto deltas de streaming
 * quando a task roda numa WAVE PARALELA (fix B): duas tasks escrevendo na MESMA
 * bolha viva viraria texto embaralhado; os tool cards + progresso continuam.
 * @param {string} taskId
 * @param {(evt:object)=>void} onEvent      sink principal do turno
 * @param {{ dispatchProgress?: (p:object)=>void, suppressDeltas?: boolean, namespaceToolIds?: boolean }} [opts]
 * @returns {(evt:object)=>void}
 */
export function makeDagTaskEventSink(taskId, onEvent, { dispatchProgress, suppressDeltas = false, namespaceToolIds = false } = {}) {
  return (evt) => {
    if (!evt) return;
    const p = typeof dispatchProgress === 'function' ? loopEventToTodoProgress(taskId, evt) : null;
    if (p) { try { dispatchProgress(p); } catch { /* board é best-effort */ } }
    if (suppressDeltas && (evt.type === 'token' || evt.type === 'reasoning')) return;
    // Wave paralela: dois loops concorrentes podem emitir o MESMO tool_call id (ex.:
    // 'call_0' de provedores que numeram do zero) — o feed usa evt.id como chave
    // (toolMsgIds) e o tool_result patcharia o CARD ERRADO. Prefixar com o taskId
    // isola os namespaces; serial fica intocado (ids byte-idênticos).
    let out = evt;
    if (namespaceToolIds && (evt.type === 'tool_call' || evt.type === 'tool_result') && evt.id != null) {
      out = { ...evt, id: `${taskId}:${evt.id}` };
    }
    onEvent(out);
  };
}

/**
 * fix B — concorrência da wave por ROTA: modelo LOCAL (LM Studio/ollama em localhost)
 * serializa na GPU — paralelizar só embola (e pode derrubar o servidor local); cloud
 * aguenta fan-out. Puro; o cap default 3 mantém o fan-out humilde (feed legível).
 * @param {{ kind?: string, baseUrl?: string }} route
 * @param {{ cap?: number }} [opts]
 * @returns {number}
 */
export function dagConcurrencyForRoute(route, { cap = 3 } = {}) {
  // route.kind é o sinal CANÔNICO (detectRoute): adapter local com IP de LAN (ex.
  // LM Studio em 192.168.x.x) não casa regex de localhost mas É local — review
  // adversarial. A regex fica de fallback pra rotas sem kind.
  if (route?.kind === 'local') return 1;
  const u = String(route?.baseUrl || '');
  const isLocal = /localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\]/i.test(u);
  return isLocal ? 1 : Math.max(1, Math.floor(Number(cap) || 1));
}

/**
 * Extrai o bloco "Respostas do usuário:" do texto de aprovação do plano (se houver),
 * pra prefixar as decisões do usuário em cada task do DAG (#5). '' quando ausente.
 * @param {string} text
 * @returns {string}
 */
export function extractUserDecisions(text) {
  if (typeof text !== 'string' || !text) return '';
  const m = /Respostas do usuário:[\s\S]*?(?=\n\n|$)/.exec(text);
  return m ? m[0].trim() : '';
}
